import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import delay from 'delay';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import * as Types from 'pages/Pages/typings';
import { Field } from '@sift/types';
import isFieldValidConfigOption from 'helpers/isFieldValidConfigOption';
import getOnboardingTasks from 'fetches/getOnboardingTasks';

import TextField from '@material-ui/core/TextField';
import CancelButton from 'components/Page/CancelButton';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SlideContent from './SlideContent';
import LoadingButton from 'atoms/LoadingButton';

interface Props {
  classes: any;
  loading: boolean;
  onSave: (data: Types.NewEntityType) => void;
  onSetOpen: (status: boolean) => void;
  setSlideIndex: (slideIndex: number) => void;
}

function ExistingAttributeSlide({ classes, loading, onSave, setSlideIndex, onSetOpen }: Props) {
  const entityTypes = useEntityTypes();
  const dispatch = useDispatch();
  const userConfig = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);
  const { _fields } = userConfig;

  const configFieldCollection = useMemo(
    () =>
      Object.values(_fields)
        .filter((field: Field) => isFieldValidConfigOption(field, { excludeChildNameFields: true }))
        .filter(
          (field: Field) =>
            field.deletable &&
            (field.type === 'text' || _get(field, 'item.type') === 'text') &&
            field.baseField !== 'messageAddress',
        ) as Field[],
    [_fields],
  );

  const availableAttributes = _sortBy(configFieldCollection, field => {
    return field.displayName.toLowerCase();
  });

  const [attribute, setAttribute] = useState<Field | null>(null);

  const handleSave = async () => {
    attribute &&
      onSave({
        name: null,
        newPersonFieldType: null,
        convertPersonField: _get(attribute, 'objectKey'),
        parentFieldId: null,
      });

    await delay(8000);
    dispatch(getOnboardingTasks());
  };

  const handleSelectValue = (event: React.ChangeEvent<{}>, newValue: Field | null) => {
    setAttribute(newValue);
  };

  return (
    <SlideContent
      actions={
        <>
          <CancelButton disabled={loading} onClick={() => setSlideIndex(1)} />
          <LoadingButton
            color="secondary"
            disabled={!attribute}
            loading={loading}
            label="Create Page Type"
            onClick={handleSave}
          />
        </>
      }
      renderBackIcon={true}
      renderCloseIcon={true}
      slideTitle="Convert an Existing Attribute"
      onSetOpen={onSetOpen}
      setSlideIndex={setSlideIndex}
    >
      <Typography className={classes.marginBottom2} variant="subtitle2">
        Choose a Short Text or Text (Chip) Collection attribute to transform to a page attribute. If
        the attribute already has existing values, we will automatically create pages for each
        value. If this attribute is mapped from a data source, pages will be automatically created
        and deleted during data sync.
      </Typography>
      {!!availableAttributes.length && (
        <Autocomplete
          data-testid="existing-autocomplete"
          id="existing-attribute-id"
          options={availableAttributes}
          getOptionLabel={option => option.displayName}
          getOptionSelected={(option: Field) =>
            _get(attribute, 'displayName') === option.displayName
          }
          renderOption={(option, { selected }) => (
            <>
              <Radio checked={selected} className={classes.marginRight} />
              {option.displayName}
            </>
          )}
          onChange={handleSelectValue}
          fullWidth={true}
          placeholder="Choose an attribute"
          renderInput={params => <TextField {...params} />}
          openOnFocus={true}
        />
      )}
    </SlideContent>
  );
}

export default ExistingAttributeSlide;
